import { useState } from 'react';
import * as api from '../lib/api';
import { haptic } from '../lib/telegram';
import { getLang, setLang, useLang, type Lang } from '../lib/i18n';

/**
 * RU / EN, at the top of the app.
 *
 * Switches the screen immediately and saves in the background. The choice
 * has to reach the server because most of what a client reads arrives in
 * the bot, not here — a language that only lived in the browser would
 * leave their reminders in the wrong one.
 *
 * If the save fails the switch goes back, rather than leaving the screen
 * saying one thing and the next reminder saying another.
 */
export function LanguageSwitch({ onChanged }: { onChanged?: () => void }) {
  const lang = useLang();
  const [busy, setBusy] = useState(false);

  async function choose(next: Lang) {
    if (next === lang || busy) return;
    const previous = getLang();
    setLang(next);           // the screen changes now, not after the round trip
    setBusy(true);
    try {
      await api.setLanguage(next);
      haptic.tap();
      onChanged?.();
    } catch {
      setLang(previous);
      haptic.error();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="langswitch">
      {(['en', 'ru'] as const).map((l) => (
        <button
          key={l}
          className="langswitch__opt"
          aria-pressed={lang === l}
          disabled={busy}
          onClick={() => choose(l)}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
